
import { supabase } from '@/integrations/supabase/client';

/**
 * Appelle la fonction RPC Supabase pour bloquer un utilisateur.
 * @param blockedUserId L'ID de l'utilisateur à bloquer.
 */
export const blockUser = async (blockedUserId: string): Promise<{ success: boolean; error?: any }> => {
  try {
    const { data, error } = await supabase.rpc('block_user', { target_user_id: blockedUserId });

    if (error) {
      console.error('Error blocking user:', error);
      return { success: false, error };
    }

    console.log('User blocked successfully:', data); // data peut contenir un message de succès de la fonction RPC
    return { success: true };
  } catch (error) {
    console.error('Client-side error calling block_user RPC:', error);
    return { success: false, error };
  }
};

/**
 * Appelle la fonction RPC Supabase pour débloquer un utilisateur.
 * @param blockedUserId L'ID de l'utilisateur à débloquer.
 */
export const unblockUser = async (blockedUserId: string): Promise<{ success: boolean; error?: any }> => {
  try {
    const { data, error } = await supabase.rpc('unblock_user', { target_user_id: blockedUserId });

    if (error) {
      console.error('Error unblocking user:', error);
      return { success: false, error };
    }

    console.log('User unblocked successfully:', data);
    return { success: true };
  } catch (error) {
    console.error('Client-side error calling unblock_user RPC:', error);
    return { success: false, error };
  }
};

/**
 * Vérifie si une relation de blocage existe entre l'utilisateur actuel et un autre utilisateur.
 * NOTE : Normalement, les RLS devraient empêcher de voir les données si bloqué.
 * Cette fonction est une VÉRIFICATION CÔTÉ CLIENT et ne doit pas être la seule mesure de sécurité.
 * Elle peut être utile pour afficher l'état de blocage dans l'UI.
 * @param userId L'ID de l'utilisateur actuel.
 * @param partnerId L'ID de l'autre utilisateur.
 * @returns 'blocked_by_partner' | 'blocked_by_user' | 'none'
 */
export const checkBlockStatus = async (userId: string, partnerId: string): Promise<'blocked_by_partner' | 'blocked_by_user' | 'none'> => {
  try {
    // Vérifie si le partenaire a bloqué l'utilisateur actuel
    const { data: blockedByPartner, error: error1 } = await supabase
      .from('blocked_relationships')
      .select('blocker_id')
      .eq('blocker_id', partnerId)
      .eq('blocked_id', userId)
      .maybeSingle();

    if (error1) throw error1;
    if (blockedByPartner) return 'blocked_by_partner';

    // Vérifie si l'utilisateur actuel a bloqué le partenaire
    const { data: blockedByUser, error: error2 } = await supabase
      .from('blocked_relationships')
      .select('blocker_id')
      .eq('blocker_id', userId)
      .eq('blocked_id', partnerId)
      .maybeSingle();

    if (error2) throw error2;
    if (blockedByUser) return 'blocked_by_user';

    return 'none';
  } catch (error) {
    console.error('Error checking block status:', error);
    // En cas d'erreur, on suppose qu'il n'y a pas de blocage pour ne pas bloquer l'UI par erreur
    return 'none'; 
  }
}; 
